import { createContext, useEffect, useState, useRef } from "react";
import {
  useRouteContext,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { FileSubmission, FileStatusEnum } from "@/models/file-submission.tsx";
import { invalidateLeaData, useFileSubmissionsData } from "@/queries";
import ToastContainer from "react-bootstrap/ToastContainer";
import {
  SubmissionToast,
  ToastInfo,
} from "../records/child/add-child-wizard/SubmissionToast";

export type FileSubmissionsContextData = {
  pendingFileSubmissions: FileSubmission[];
  addPendingFileSubmission: (
    fs: FileSubmission,
    customSuccessMessage?: string
  ) => void;
  showToast: (
    type: "success" | "error" | "warning" | "processing",
    title: string,
    message: string,
    id?: string
  ) => void;
  removeToast: (id: string) => void;
};

export const FileSubmissionsContext = createContext<FileSubmissionsContextData>(
  {
    pendingFileSubmissions: [],
    addPendingFileSubmission: (
      fs: FileSubmission,
      customSuccessMessage?: string
    ) => {},
    showToast: (type, title, message, id) => {},
    removeToast: (id) => {},
  }
);

function condenseDeleteMessages(messages: string[]): string {
  if (messages.length === 0) return "";
  if (messages.length === 1) return messages[0];

  const deletePattern = /^(.+?)\s+records?\s+deleted\s+successfully\.?$/i;
  const nouns: string[] = [];
  for (const m of messages) {
    const match = m.match(deletePattern);
    if (!match) return messages.join("\n");
    nouns.push(match[1].trim());
  }

  const uniqueNouns = [...new Set(nouns.map((n) => n.toLowerCase()))].map(
    (lower) => nouns.find((n) => n.toLowerCase() === lower)!
  );

  if (uniqueNouns.length === 1) {
    return `${uniqueNouns[0]} records deleted successfully.`;
  }
  if (uniqueNouns.length === 2) {
    return `${uniqueNouns[0]} and ${uniqueNouns[1]} records deleted successfully.`;
  }
  const last = uniqueNouns[uniqueNouns.length - 1];
  const rest = uniqueNouns.slice(0, -1).join(", ");
  return `${rest}, and ${last} records deleted successfully.`;
}

export function FileSubmissionsTracker({
  children,
}: {
  children: React.ReactNode;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const navigate = useNavigate();
  const { leaid } = useParams({ from: "/$leaid" });

  const [pendingFileSubmissions, setPendingFileSubmissions] = useState<
    FileSubmission[]
  >([]);
  const [toasts, setToasts] = useState<ToastInfo[]>([]);

  // Track processed submissions to avoid duplicate toasts
  const processedIds = useRef<Set<string>>(new Set());

  const customSuccessMessages = useRef<Map<string, string>>(new Map());

  const invalidateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    return () => {
      if (invalidateTimeoutRef.current) {
        clearTimeout(invalidateTimeoutRef.current);
      }
    };
  }, []);

  // Batch tracking - collect results before showing toast
  const batchResults = useRef<{
    succeeded: string[];
    failed: string[];
    customMessages: string[];
  }>({ succeeded: [], failed: [], customMessages: [] });

  const addPendingFileSubmission = (
    fs: FileSubmission,
    customSuccessMessage?: string
  ) => {
    if (customSuccessMessage) {
      customSuccessMessages.current.set(fs.Id, customSuccessMessage);
    }
    setPendingFileSubmissions((prev) => [...prev, fs]);
  };

  // Show an immediate toast message
  const showToast = (
    type: "success" | "error" | "warning" | "processing",
    title: string,
    message: string,
    id?: string
  ) => {
    const toastId = id ?? `toast-${Date.now()}`;
    setToasts((prev) => [
      ...prev.filter((t) => t.id !== toastId),
      {
        id: toastId,
        show: true,
        type,
        title,
        message,
      },
    ]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const showSummaryToast = (
    succeeded: string[],
    failed: string[],
    customMessages: string[]
  ) => {
    const total = succeeded.length + failed.length;

    let type: "success" | "error" | "warning";
    let title: string;
    let message: string;

    if (failed.length === 0) {
      type = "success";
      title = "Success";

      if (customMessages.length > 0) {
        message = condenseDeleteMessages(customMessages);
      } else {
        message =
          total === 1
            ? `${succeeded[0]} record submitted successfully.`
            : `All ${total} records submitted successfully: ${succeeded.join(", ")}.`;
      }
    } else if (succeeded.length === 0) {
      type = "error";
      title = "Submission Failed";
      message =
        total === 1
          ? `${failed[0]} record failed validation.`
          : `All ${total} records failed: ${failed.join(", ")}.`;
    } else {
      type = "warning";
      title = "Partial Success";
      message = `${succeeded.length} of ${total} records submitted. Failed: ${failed.join(", ")}.`;
    }

    setToasts((prev) => [
      ...prev,
      {
        id: `summary-${Date.now()}`,
        show: true,
        type,
        title,
        message,
      },
    ]);
  };

  const refreshInterval = pendingFileSubmissions.length > 0 ? 5000 : 3600000;
  const submissionDataQuery = useFileSubmissionsData(
    leaContext.currentUserOrg.esiIdPath,
    refreshInterval
  );

  useEffect(() => {
    if (submissionDataQuery.data === undefined) return;
    if (pendingFileSubmissions.length === 0) return;

    const submissions = submissionDataQuery.data;
    const unprocessed: FileSubmission[] = [];
    const newSucceeded: string[] = [];
    const newFailed: string[] = [];
    const newCustomMessages: string[] = [];
    let hasNewPosted = false;

    pendingFileSubmissions.forEach((fs) => {
      if (processedIds.current.has(fs.Id)) {
        return;
      }

      const latestData = submissions.find((x) => x.Id === fs.Id);

      if (latestData?.CurrentStatus === FileStatusEnum.Posted) {
        newSucceeded.push(latestData.RecordTypeCode);

        const customMessage = customSuccessMessages.current.get(fs.Id);
        if (customMessage) {
          newCustomMessages.push(customMessage);
          customSuccessMessages.current.delete(fs.Id);
        }

        processedIds.current.add(fs.Id);
        hasNewPosted = true;
      } else if (latestData?.CurrentStatus === FileStatusEnum.Rejected) {
        newFailed.push(latestData.RecordTypeCode);
        customSuccessMessages.current.delete(fs.Id);
        processedIds.current.add(fs.Id);
      } else if (latestData?.CurrentStatus === FileStatusEnum.Failed) {
        newFailed.push(latestData.RecordTypeCode);
        customSuccessMessages.current.delete(fs.Id);
        processedIds.current.add(fs.Id);
      } else {
        unprocessed.push(fs);
      }
    });

    if (pendingFileSubmissions.length !== unprocessed.length) {
      setPendingFileSubmissions(unprocessed);
    }

    if (newSucceeded.length > 0 || newFailed.length > 0) {
      batchResults.current.succeeded.push(...newSucceeded);
      batchResults.current.failed.push(...newFailed);
      batchResults.current.customMessages.push(...newCustomMessages);
    }

    // Check if CHLD just posted but other records are still processing
    if (newSucceeded.includes("CHLD") && unprocessed.length > 0) {
      setToasts((prev) => [
        ...prev,
        {
          id: `chld-posted-${Date.now()}`,
          show: true,
          type: "success",
          title: "CHLD Record Posted",
          message: "Processing additional records...",
        },
      ]);
    }

    // Only invalidate and show final toast when ALL records are done
    if (unprocessed.length === 0 && pendingFileSubmissions.length > 0) {
      const { succeeded, failed, customMessages } = batchResults.current;

      if (succeeded.length > 0 || failed.length > 0) {
        setToasts((prev) =>
          prev.filter((t) => !t.id.startsWith("processing-"))
        );

        showSummaryToast(succeeded, failed, customMessages);
        batchResults.current = {
          succeeded: [],
          failed: [],
          customMessages: [],
        };
        processedIds.current.clear();
      }

      if (succeeded.length > 0) {
        if (invalidateTimeoutRef.current) {
          clearTimeout(invalidateTimeoutRef.current);
        }
        invalidateTimeoutRef.current = setTimeout(() => {
          invalidateLeaData(
            leaContext.queryClient,
            leaContext.currentUserOrg.esiIdPath
          );
        }, 1000);
      }
    }
  }, [pendingFileSubmissions, submissionDataQuery.data]);

  return (
    <FileSubmissionsContext.Provider
      value={{
        pendingFileSubmissions,
        addPendingFileSubmission,
        showToast,
        removeToast,
      }}
    >
      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ zIndex: 9999 }}
      >
        {toasts.map((toast) => (
          <SubmissionToast key={toast.id} toast={toast} onClose={removeToast} />
        ))}
      </ToastContainer>

      {children}
    </FileSubmissionsContext.Provider>
  );
}
